import { useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Alert, Badge, Button, Tabs, ButtonLink, TextLink } from '@shared/components';
import { PermissionGuard } from '@/features/auth';
import { useServiceQuery } from '../hooks/useServiceQuery';
import { useServiceMutations } from '../hooks/useServiceMutations';
import { ServiceItemPanel } from '../components/ServiceItemPanel';
import { ServiceCostSummary } from '../components/ServiceCostSummary';
import type { BackRef } from '../types/backRef';

type Tab = 'details' | 'items';

const TABS: { id: Tab; label: string }[] = [
    { id: 'details', label: 'Details' },
    { id: 'items', label: 'Items' },
];

function statusVariant(status?: string): 'success' | 'error' | 'info' {
    if (status === 'active') return 'success';
    if (status === 'retired') return 'error';
    return 'info';
}

export function ServiceDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { state } = useLocation();
    const backRef = (state as { backRef?: BackRef } | null)?.backRef;
    const [activeTab, setActiveTab] = useState<Tab>('details');
    const query = useServiceQuery(id);
    const { retireMutation } = useServiceMutations();

    if (!id) return <p className="text-mp-error">Invalid service ID.</p>;
    if (query.isLoading) return <p>Loading service...</p>;
    if (query.isError || !query.data) {
        return <Alert variant="error"><span>Service not found or failed to load.</span></Alert>;
    }

    const service = query.data;

    const fallbackBackRef: BackRef = service.plan
        ? { path: `/smr-admin/plans/${service.plan.id}?tab=services`, label: service.plan.name }
        : { path: '/smr-admin/products', label: 'Products' };

    const effectiveBackRef = backRef ?? fallbackBackRef;

    const handleRetire = () => {
        retireMutation.mutate(id!, {
            onSuccess: () => navigate(effectiveBackRef.path, { state: effectiveBackRef.state }),
        });
    };

    return (
        <section className="space-y-4">
            <div className="flex items-center gap-2">
                <TextLink to={effectiveBackRef.path} state={effectiveBackRef.state} color="secondary" className="text-sm">
                    &larr; Back to {effectiveBackRef.label}
                </TextLink>
            </div>

            <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-3">
                    <h1 className="text-2xl font-semibold">{service?.name ?? `Service ${id}`}</h1>
                    {service?.status && (
                        <Badge size="xs" variant={statusVariant(service.status)}>{service.status}</Badge>
                    )}
                </div>
                <PermissionGuard permission={{ resource: 'smr', action: 'service.edit' }}>
                    <ButtonLink
                        to={`/smr-admin/services/${id}/edit`}
                        state={{ backRef: effectiveBackRef }}
                        variant="outline" size="xs"
                    >
                        Edit Service
                    </ButtonLink>
                </PermissionGuard>
            </div>

            <Tabs
                tabs={TABS}
                activeTab={activeTab}
                onTabChange={setActiveTab}
                idPrefix="service-detail"
                ariaLabel="Service sections"
            >
                {activeTab === 'details' && service && (
                    <div className="space-y-6 max-w-2xl">
                        <table className="w-full text-sm">
                            <tbody>
                                <tr className="border-b border-mp-border">
                                    <th className="w-40 px-3 py-2 text-left">ID</th>
                                    <td className="px-3 py-2 text-left font-mono text-xs">{service.id}</td>
                                </tr>
                                <tr className="border-b border-mp-border">
                                    <th className="px-3 py-2 text-left">Name</th>
                                    <td className="px-3 py-2 text-left font-semibold">{service.name}</td>
                                </tr>
                                <tr className="border-b border-mp-border">
                                    <th className="px-3 py-2 text-left">Description</th>
                                    <td className="px-3 py-2 text-left">{service.description || <span className="text-mp-muted">—</span>}</td>
                                </tr>
                                <tr className="border-b border-mp-border">
                                    <th className="px-3 py-2 text-left">Plan</th>
                                    <td className="px-3 py-2 text-left">
                                        {service.plan ? (
                                            <TextLink to={`/smr-admin/plans/${service.plan.id}?tab=services`} color="secondary">
                                                {service.plan.name}
                                            </TextLink>
                                        ) : (
                                            <span className="text-mp-muted">—</span>
                                        )}
                                    </td>
                                </tr>
                                <tr className="border-b border-mp-border">
                                    <th className="px-3 py-2 text-left">Position</th>
                                    <td className="px-3 py-2 text-left">{service.position ?? <span className="text-mp-muted">—</span>}</td>
                                </tr>
                                <tr className="border-b border-mp-border">
                                    <th className="px-3 py-2 text-left">Service Type</th>
                                    <td className="px-3 py-2 text-left">{service.service_type ?? <span className="text-mp-muted">—</span>}</td>
                                </tr>
                                <tr className="border-b border-mp-border">
                                    <th className="px-3 py-2 text-left">Months from start</th>
                                    <td className="px-3 py-2 text-left">{service.months_from_start ?? <span className="text-mp-muted">—</span>}</td>
                                </tr>
                                <tr className="border-b border-mp-border">
                                    <th className="px-3 py-2 text-left">Mileage from start</th>
                                    <td className="px-3 py-2 text-left">{service.mileage_from_start ?? <span className="text-mp-muted">—</span>}</td>
                                </tr>
                                <tr className="border-b border-mp-border">
                                    <th className="px-3 py-2 text-left">Status</th>
                                    <td className="px-3 py-2 text-left">
                                        <Badge size="xs" variant={statusVariant(service.status)}>
                                            {service.status}
                                        </Badge>
                                    </td>
                                </tr>
                                <tr className="border-b border-mp-border">
                                    <th className="px-3 py-2 text-left">Created</th>
                                    <td className="px-3 py-2 text-left">{new Date(service.created_at).toLocaleString()}</td>
                                </tr>
                                <tr className="border-b border-mp-border">
                                    <th className="px-3 py-2 text-left">Updated</th>
                                    <td className="px-3 py-2 text-left">{new Date(service.updated_at).toLocaleString()}</td>
                                </tr>
                            </tbody>
                        </table>

                        {service.cost_breakdown && (
                            <div className="border-t border-mp-border pt-6">
                                <ServiceCostSummary breakdown={service.cost_breakdown} />
                            </div>
                        )}

                        <PermissionGuard permission={{ resource: 'smr', action: 'service.delete' }}>
                            <div className="border-t border-mp-border pt-6">
                                <h3 className="font-semibold mb-2">Retire Service</h3>
                                <div className="flex items-start gap-4 justify-between">
                                    <p className="text-sm text-mp-muted">
                                        Retiring a service removes it from active use. This cannot be undone.
                                    </p>
                                    <Button
                                        size="sm"
                                        variant="warning"
                                        type="button"
                                        onClick={handleRetire}
                                        disabled={retireMutation.isPending || service.status === 'retired'}
                                        className="whitespace-nowrap"
                                    >
                                        {retireMutation.isPending ? 'Retiring...' : 'Retire Service'}
                                    </Button>
                                </div>
                            </div>
                        </PermissionGuard>
                    </div>
                )}
                {activeTab === 'items' && id && <ServiceItemPanel serviceId={id} costMode={service.cost_mode} />}
            </Tabs>
        </section>
    );
}
